import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import MainLayout from '../components/Layout/MainLayout';
import { User, Edit, Phone, MapPin, Mail, Bell, Shield, ChevronRight, Settings as SettingsIcon, Cookie } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { fetchJson } from '@/lib/apiError';
import AppearanceCard from '@/components/settings/AppearanceCard';
import NotificationSettingsCard from '@/components/settings/NotificationSettingsCard';

const Settings: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [profile, setProfile] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [editForm, setEditForm] = useState({
    first_name: '',
    last_name: '',
    whatsapp_number: ''
  });
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (user?.id) {
      fetchProfile();
    }
  }, [user]);

  const fetchProfile = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session && user?.email === 'demo@sme.ng') {
        console.log('No session, using Edge Function for demo user');
        
        // Track demo user login
        try {
          await supabase.from('login_history').insert({
            user_id: '11111111-2222-3333-4444-555555555555',
            ip_address: '0.0.0.0',
            user_agent: navigator.userAgent,
            success: true
          });
        } catch (err) {
          console.log('Login tracking error (non-critical):', err);
        }
        
        // fetchJson: status/network/JSON-parse handling + 15s timeout so a hung
        // request can't leave the profile loader stuck forever.
        try {
          const profileData = await fetchJson<any>(
            `https://sjszhatwjwafioakcncx.supabase.co/functions/v1/get-profile?email=demo@sme.ng`,
            {
              timeoutMs: 15000,
              headers: {
                'Authorization': `Bearer eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InNqc3poYXR3andhZmlvYWtjbmN4Iiwicm9sZSI6ImFub24iLCJpYXQiOjE3NTAxMDU5NjAsImV4cCI6MjA2NTY4MTk2MH0.auq6Tek2SaVWjCSt6ew-VQEX21sV0Dx7YJU1vWNTjMA`
              }
            }
          );
          setProfile(profileData);
        } catch (e) {
          console.error('Edge Function error:', e);
          setProfile(null);
        }
        return;
      }

      if (!session) {
        console.log('No session, please sign in');
        setProfile(null);
        return;
      }

      const { data: baseUser, error: baseErr } = await supabase
        .from('users')
        .select(`
          first_name,
          last_name,
          email,
          whatsapp_number,
          client_number,
          created_at,
          last_login,
          last_login_ip,
          last_login_user_agent,
          login_count
        `)
        .eq('id', user?.id)
        .maybeSingle();

      const { data: ua, error: uaErr } = await supabase
        .from('user_apartments')
        .select(`
          apartment_id,
              apartments (
                id,
                apartment_number,
                buildings (
                  id,
                  name,
                  address
                )
              )
        `)
        .eq('user_id', user?.id)
        .limit(1)
        .maybeSingle();

      console.log('Profile queries:', { baseUser, baseErr, ua, uaErr });

      if (baseErr) {
        console.error('Error fetching base user:', baseErr);
        setProfile(null);
      } else {
        const apartmentData = ua?.apartments ?? null;
        const buildingData = apartmentData?.buildings ?? null;

        setProfile({
          first_name: baseUser?.first_name,
          last_name: baseUser?.last_name,
          email: baseUser?.email,
          whatsapp_number: baseUser?.whatsapp_number ?? null,
          client_number: baseUser?.client_number ?? 'Not assigned',
          created_at: baseUser?.created_at,
          last_login: baseUser?.last_login,
          last_login_ip: baseUser?.last_login_ip,
          last_login_user_agent: baseUser?.last_login_user_agent,
          login_count: baseUser?.login_count,
          buildings: buildingData || null,
          apartment: apartmentData || null
        });
      }
    } catch (error) {
      console.error('Error fetching profile:', error);
      setProfile(null);
    } finally {
      setLoading(false);
    }
  };

  const handleEditClick = () => {
    setEditForm({
      first_name: profile?.first_name || '',
      last_name: profile?.last_name || '',
      whatsapp_number: profile?.whatsapp_number || ''
    });
    setIsEditDialogOpen(true);
  };

  const handleSaveProfile = async () => {
    if (!user?.id) return;

    setIsSaving(true);
    try {
      const { error } = await supabase
        .from('users')
        .update({
          first_name: editForm.first_name.trim(),
          last_name: editForm.last_name.trim(),
          whatsapp_number: editForm.whatsapp_number.trim() || null
        })
        .eq('id', user.id);

      if (error) throw error;

      toast.success(t('settings.profileUpdated'));
      setIsEditDialogOpen(false);
      fetchProfile();
    } catch (error) {
      console.error('Error updating profile:', error);
      toast.error(t('settings.profileUpdateFailed'));
    } finally {
      setIsSaving(false);
    }
  };

  const settingsItems = [
    {
      icon: Bell,
      title: t('settings.notifications'),
      subtitle: t('settings.manageNotificationPrefs'),
      path: '/user/settings/notifications',
    },
    {
      icon: Shield,
      title: t('settings.security'),
      subtitle: t('settings.changePasswordAndSecurity'),
      path: '/user/settings/security',
    },
    {
      icon: MapPin,
      title: t('settings.address'),
      subtitle: t('settings.updateServiceAddress'),
      path: '/user/settings/address',
    },
    {
      icon: Cookie,
      title: t('settings.privacyData'),
      subtitle: t('settings.managePrivacyPrefs'),
      path: '/user/settings/privacy-data',
    },
  ];

  return (
    <MainLayout title={t('settings.profileSettings')}>
      <div className="p-4 space-y-6 pb-20">
        {/* Profile Header */}
        <div className="bg-gradient-to-r from-blue-500 to-blue-600 rounded-xl p-6 text-white">
          <div className="flex items-center space-x-4">
            <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center">
              <User className="w-8 h-8" />
            </div>
            <div>
              <h2 className="text-xl font-semibold">
                {loading ? t('common.loading') : profile ? `${profile.first_name || ''} ${profile.last_name || ''}`.trim() || user?.email || t('dashboard.defaultUser') : user?.email || t('dashboard.defaultUser')}
              </h2>
              <p className="text-blue-100">{t('settings.activeMember')}</p>
            </div>
          </div>
        </div>

        {/* Appearance (theme) */}
        <AppearanceCard />

        {/* Notification channels */}
        <NotificationSettingsCard />

        {/* Contact Information */}
        <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{t('settings.contactInformation')}</h3>
            <Button variant="outline" size="sm" onClick={handleEditClick}>
              <Edit className="w-4 h-4 mr-2" />
              {t('common.edit')}
            </Button>
          </div>
          
          <div className="space-y-4">
            <div className="flex items-center space-x-3">
              <Mail className="w-5 h-5 text-gray-400" />
              <div>
                <p className="font-medium text-gray-900 dark:text-white">{t('common.email')}</p>
                <p className="text-gray-600 dark:text-gray-400">{profile?.email || user?.email || t('common.notProvided')}</p>
              </div>
            </div>

            <div className="flex items-center space-x-3">
              <Phone className="w-5 h-5 text-green-500" />
              <div>
                <p className="font-medium text-gray-900 dark:text-white">{t('settings.whatsappNumber')}</p>
                <p className="text-gray-600 dark:text-gray-400">{profile?.whatsapp_number || t('common.notProvided')}</p>
              </div>
            </div>

            <div className="flex items-center space-x-3">
              <Phone className="w-5 h-5 text-gray-400" />
              <div>
                <p className="font-medium text-gray-900 dark:text-white">{t('settings.clientNumber')}</p>
                <p className="text-gray-600 dark:text-gray-400">{profile?.client_number || t('common.notAssigned')}</p>
              </div>
            </div>
            
            <div className="flex items-center space-x-3">
              <MapPin className="w-5 h-5 text-gray-400" />
              <div>
                <p className="font-medium text-gray-900 dark:text-white text-left">{t('settings.building')}</p>
                <p className="text-gray-600 dark:text-gray-400">
                  {profile?.buildings ? profile.buildings.name : t('common.notAssigned')}
                </p>
              </div>
            </div>
            
            <div className="flex items-center space-x-3">
              <MapPin className="w-5 h-5 text-gray-400" />
              <div>
                <p className="font-medium text-gray-900 dark:text-white text-left">{t('settings.apartment')}</p>
                <p className="text-gray-600 dark:text-gray-400">
                  {profile?.apartment ? t('settings.apartmentLabel', { number: profile.apartment.apartment_number }) : t('common.notAssigned')}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Account Stats */}
        <div className="bg-white dark:bg-gray-800 rounded-xl p-4 shadow-sm text-center">
          <p className="text-2xl font-bold text-blue-500">
            {profile?.created_at ? (() => {
              const diffMs = new Date().getTime() - new Date(profile.created_at).getTime();
              const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
              const diffMonths = Math.floor(diffDays / 30);
              const diffYears = Math.floor(diffDays / 365);
              
              if (diffYears >= 1) return diffYears;
              if (diffMonths >= 1) return diffMonths;
              if (diffDays >= 1) return diffDays;
              return Math.floor(diffMs / (1000 * 60 * 60));
            })() : '8'}
          </p>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            {profile?.created_at ? (() => {
              const diffMs = new Date().getTime() - new Date(profile.created_at).getTime();
              const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
              const diffMonths = Math.floor(diffDays / 30);
              const diffYears = Math.floor(diffDays / 365);
              
              if (diffYears >= 1) return diffYears === 1 ? t('settings.yearWithUs') : t('settings.yearsWithUs');
              if (diffMonths >= 1) return diffMonths === 1 ? t('settings.monthWithUs') : t('settings.monthsWithUs');
              if (diffDays >= 1) return diffDays === 1 ? t('settings.dayWithUs') : t('settings.daysWithUs');
              return t('settings.hoursWithUs');
            })() : t('settings.monthsWithUs')}
          </p>
        </div>

        {/* Login Details */}
        <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">{t('settings.loginInformation')}</h3>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600 dark:text-gray-400">{t('settings.lastLogin')}</span>
              <span className="text-sm font-medium text-gray-900 dark:text-white">
                {profile?.last_login ? new Date(profile.last_login).toLocaleString() : t('settings.never')}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600 dark:text-gray-400">{t('settings.totalLogins')}</span>
              <span className="text-sm font-medium text-gray-900 dark:text-white">
                {profile?.login_count || 0}
              </span>
            </div>
            {profile?.last_login_ip && (
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600 dark:text-gray-400">{t('settings.lastIp')}</span>
                <span className="text-sm font-medium text-gray-900 dark:text-white">
                  {profile.last_login_ip}
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Settings Section */}
        <div className="bg-white dark:bg-gray-800 rounded-xl p-4 shadow-sm">
          <div className="flex items-center space-x-3 mb-4">
            <SettingsIcon className="w-6 h-6 text-gray-600 dark:text-gray-400" />
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{t('settings.title')}</h3>
          </div>
          <div className="space-y-1">
            {settingsItems.map((item, index) => {
              const Icon = item.icon;
              return (
                <button
                  key={index}
                  onClick={() => navigate(item.path)}
                  className="w-full flex items-center space-x-4 p-4 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                >
                  <div className="w-10 h-10 bg-gray-100 dark:bg-gray-700 rounded-lg flex items-center justify-center">
                    <Icon size={20} className="text-gray-600 dark:text-gray-400" />
                  </div>
                  <div className="flex-1 text-left">
                    <p className="font-medium text-gray-900 dark:text-white">{item.title}</p>
                    <p className="text-sm text-gray-500 dark:text-gray-400">{item.subtitle}</p>
                  </div>
                  <ChevronRight size={20} className="text-gray-400" />
                </button>
              );
            })}
          </div>
        </div>

        {/* Edit Profile Dialog */}
        <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{t('settings.editProfile')}</DialogTitle>
              <DialogDescription>
                {t('settings.updateProfileInfo')}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label htmlFor="first_name">{t('settings.firstName')}</Label>
                <Input
                  id="first_name"
                  value={editForm.first_name}
                  onChange={(e) => setEditForm({ ...editForm, first_name: e.target.value })}
                  placeholder={t('settings.enterFirstName')}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="last_name">{t('settings.lastName')}</Label>
                <Input
                  id="last_name"
                  value={editForm.last_name}
                  onChange={(e) => setEditForm({ ...editForm, last_name: e.target.value })}
                  placeholder={t('settings.enterLastName')}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="whatsapp_number">{t('settings.whatsappNumberOptional')}</Label>
                <Input
                  id="whatsapp_number"
                  type="tel"
                  value={editForm.whatsapp_number}
                  onChange={(e) => setEditForm({ ...editForm, whatsapp_number: e.target.value })}
                  placeholder={t('settings.whatsappPlaceholder')}
                />
              </div>
              <div className="flex justify-end gap-2 mt-6">
                <Button variant="outline" onClick={() => setIsEditDialogOpen(false)}>
                  {t('common.cancel')}
                </Button>
                <Button onClick={handleSaveProfile} disabled={isSaving}>
                  {isSaving ? t('common.saving') : t('settings.saveChanges')}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </MainLayout>
  );
};

export default Settings;
